import axios from 'axios';
import { getApiV1BaseUrl } from '../config';
import { AUTH_CHECK_EVENT, dispatchAppEvent, readStoredUser, tokenUserId } from './authEvents';

// API Configuration: REACT_APP_API_URL (defaults to /api) + /v1
const API_BASE_URL = getApiV1BaseUrl();

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 seconds timeout
  withCredentials: true, // send/receive the httpOnly refresh cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const raw = localStorage.getItem('access_token');
    const token = raw && raw !== 'null' && raw !== 'undefined' ? raw : null;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Single shared in-flight refresh so parallel 401s trigger only one
 * POST /auth/refresh call. Resolves with the new access token; rejects if
 * the refresh itself failed (expired/invalid/no cookie).
 */
let refreshPromise = null;

function requestRefresh() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API_BASE_URL}/auth/refresh`, null, {
        withCredentials: true,
        headers: { 'X-Requested-With': 'zaynic' },
      })
      .then((res) => {
        const newToken = res.data?.access_token;
        if (!newToken) throw new Error('No access_token in refresh response');
        // The refresh cookie is shared by every tab, so after a login in
        // another tab it belongs to THAT account. Never silently continue
        // as someone else (R0): keep the browser's real session, reload the
        // user (which shows a notice) and fail the original request.
        const storedUser = readStoredUser();
        const newUserId = tokenUserId(newToken);
        localStorage.setItem('access_token', newToken);
        if (storedUser && newUserId !== null && storedUser.id !== newUserId) {
          dispatchAppEvent(AUTH_CHECK_EVENT, { reason: 'identity-changed' });
          throw new Error('Session now belongs to a different account');
        }
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function isAuthRoute(url) {
  return !!url && (url.includes('/auth/refresh') || url.includes('/auth/login'));
}

// Response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalConfig = error.config;

    // One retry-after-refresh attempt per request, skipping the refresh/
    // login endpoints themselves (a 401 there means "really not logged
    // in", not "access token expired").
    if (
      error.response?.status === 401 &&
      originalConfig &&
      !originalConfig._retriedAfterRefresh &&
      !isAuthRoute(originalConfig.url) &&
      localStorage.getItem('access_token')
    ) {
      try {
        const newToken = await requestRefresh();
        originalConfig._retriedAfterRefresh = true;
        originalConfig.headers = {
          ...originalConfig.headers,
          Authorization: `Bearer ${newToken}`,
        };
        return apiClient(originalConfig);
      } catch (refreshError) {
        // Refresh itself failed -- fall through to the existing
        // clear-storage behavior below exactly as before.
      }
    }

    // Handle token expiration
    if (error.response?.status === 401) {
      const hadToken = localStorage.getItem('access_token');
      // Only clear + redirect if user WAS previously authenticated (token expired)
      // Don't redirect on 401 for guests — that's expected (e.g., /cart while not logged in)
      if (hadToken) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_data');
        dispatchAppEvent(AUTH_CHECK_EVENT, { reason: 'session-expired' });
        // Don't trigger a full reload; let React router handle navigation gracefully
        // window.location.href = '/';
      }
    }

    // Global error handling - log errors and show user-friendly messages
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      // Handle specific error codes
      switch (status) {
        case 400:
          console.error('Bad Request:', data.detail || data.message || 'Invalid request');
          break;
        case 401:
          console.error('Unauthorized:', data.detail || 'Authentication required');
          break;
        case 403:
          console.error('Forbidden:', data.detail || 'Access denied');
          // Possibly a stale role or another tab's login: re-check once.
          if (localStorage.getItem('access_token') && !isAuthRoute(originalConfig?.url)) {
            dispatchAppEvent(AUTH_CHECK_EVENT, { reason: 'forbidden' });
          }
          break;
        case 404:
          console.error('Not Found:', data.detail || 'Resource not found');
          break;
        case 422:
          console.error('Validation Error:', data.detail || 'Invalid data provided');
          break;
        case 500:
          console.error('Server Error:', data.detail || 'Internal server error');
          break;
        default:
          console.error(`API Error (${status}):`, data.detail || data.message || 'Unknown error');
      }
    } else if (error.request) {
      // Network error - no response received
      console.error('Network Error:', 'Unable to connect to server. Please check your internet connection.');
    } else {
      // Something else happened
      console.error('Request Error:', error.message || 'An unexpected error occurred');
    }
    
    return Promise.reject(error);
  }
);

export default apiClient;
export { API_BASE_URL };